import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldX, Loader2, Search } from "lucide-react";
import { BrandBar } from "@/components/landing/BrandBar";
import api from "@/lib/api";

export default function Verify() {
  const [certId, setCertId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!certId.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.get(`/training/verify/${encodeURIComponent(certId.trim())}`);
      setResult(res.data);
    } catch {
      setResult({ valid: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB] flex items-center justify-center px-4 relative" data-testid="verify-page">
      <BrandBar />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Link to="/" aria-label="Back to OPERA360 home">
            <img src="/logo-full.png" alt="OPERA360" className="h-32 w-auto rounded-md bg-white p-3 shadow-2xl" data-testid="verify-logo" />
          </Link>
        </div>
        <div className="technical-card p-8">
          <div className="flex items-center gap-2.5 mb-1">
            <ShieldCheck size={14} className="text-sky-600" />
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Certificate Verification</p>
          </div>
          <p className="text-sm text-muted-foreground mb-7">Enter the certificate ID from an OPERA360 training certificate (format O360-XXXXXXXX).</p>
          <form onSubmit={check} className="flex gap-2">
            <input
              data-testid="verify-input"
              value={certId}
              onChange={(e) => setCertId(e.target.value.toUpperCase())}
              placeholder="O360-XXXXXXXX"
              className="flex-1 rounded-md border border-slate-200 bg-[#EDF4FB] px-4 py-3 text-sm font-mono uppercase tracking-wider text-slate-900 placeholder:text-muted-foreground/40 focus:outline-none focus:border-cadmium/60 transition-all"
            />
            <button
              type="submit"
              data-testid="verify-submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-3 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Verify
            </button>
          </form>

          {result && (
            <div data-testid="verify-result" className={`mt-6 rounded-md border p-5 ${result.valid ? "border-moss/40 bg-moss/10" : "border-red-500/40 bg-red-500/10"}`}>
              {result.valid ? (
                <>
                  <p className="flex items-center gap-2 text-sm font-semibold text-green-700 mb-3">
                    <ShieldCheck size={16} /> Authentic certificate
                  </p>
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><dt className="text-muted-foreground">Recipient</dt><dd className="text-slate-900 font-medium" data-testid="verify-name">{result.employee_name}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Course</dt><dd className="text-slate-900" data-testid="verify-course">{result.course_title}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Final score</dt><dd className="font-mono text-green-700">{result.score}%</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Completed</dt><dd className="font-mono text-muted-foreground">{result.completed_at}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Certificate ID</dt><dd className="font-mono text-muted-foreground">{result.certificate_id}</dd></div>
                  </dl>
                </>
              ) : (
                <p className="flex items-center gap-2 text-sm font-semibold text-red-600">
                  <ShieldX size={16} /> No valid certificate found for this ID.
                </p>
              )}
            </div>
          )}
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
            ← Back to site
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
